import styled from "@emotion/styled";
import { useConnection } from "@solana/wallet-adapter-react";
import type { ConfirmedSignatureInfo } from "@solana/web3.js";
import { Col, Row } from "antd";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { Element } from "react-scroll";

import { ReactComponent as CloseArrows } from "../../assets/close-arrows.svg";
import DashboardIcon from "../../assets/dashboard.svg";
import { ReactComponent as OpenArrows } from "../../assets/open-arrows.svg";
import ThisAccountIsEmptySteppy from "../../assets/this-is-empty-account-steppy.svg";
import { ReactComponent as UpArrow } from "../../assets/up-arrow.svg";
import { ConnectView } from "../../components/ConnectView/ConnectView";
import { Balances } from "../../components/dashboard/Balances/Balances";
import { DashboardUtils } from "../../components/dashboard/DashboardUtils";
import { DEXGrid } from "../../components/dashboard/DEXGrid";
import { FarmGrid } from "../../components/dashboard/FarmGrid";
import { Lending } from "../../components/dashboard/Lending";
import { LPGrid } from "../../components/dashboard/LPGrid";
import { ManagedFunds } from "../../components/dashboard/ManagedFunds";
import { MarginTrading } from "../../components/dashboard/MarginTrading";
import { NFTGrid } from "../../components/dashboard/NFTGrid";
import { NFTMarkets } from "../../components/dashboard/NFTMarkets";
import { PortfolioOverview } from "../../components/dashboard/PortfolioOverview";
import { RewardOptions } from "../../components/dashboard/RewardOptions/RewardOptions";
import { Staking } from "../../components/dashboard/Staking/Staking";
import { ValidatorStaking } from "../../components/dashboard/ValidatorStaking";
import { YieldAggGrid } from "../../components/dashboard/YieldAggGrid";
import { StepSolidButton } from "../../components/shared";
import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useLanguageContext } from "../../contexts/language/language";
import { useNFTContext } from "../../contexts/nft";
import { useCollapseControl } from "../../hooks/home/useCollapseControl";
import { ViewHeader } from "..";

export const HomeView: FC = () => {
  const { connection } = useConnection();
  const { connected, publicKey } = useEnrichedWallet();
  const [isEmptyAccount, setIsEmptyAccount] = useState(false);
  const { expandAllPanels, closeAllPanels, getPanelKey, onPanelClick, activePanels } = useCollapseControl();
  const {
    currentLanguageData: {
      pages: { dashboard, allPages },
    },
  } = useLanguageContext();
  const { userNFTs } = useNFTContext();

  useEffect(() => {
    if (connected && publicKey) {
      connection
        .getConfirmedSignaturesForAddress2(publicKey, { limit: 1 }, "confirmed")
        .then((confirmedSignatures: Array<ConfirmedSignatureInfo>) => {
          if (confirmedSignatures.length === 0) {
            setIsEmptyAccount(true);
          }
        })
        .catch((e) => {
          console.warn("Unexpected failure to get confirmed signatures: ", e);
        });
    }
  }, [connected, connection, publicKey]);

  if (isEmptyAccount && connected) {
    return (
      <EmptyAccountWarningContentContainer>
        <EmptyAccountWarningContent>
          <img src={ThisAccountIsEmptySteppy} alt="This account is empty sleeping Steppy mascot" />
          <div>
            <div className="bold-font-container">
              <p>{allPages.emptySOLAccount}</p>
            </div>
            <div className="paragraph-text-container">
              <p>
                {allPages.unusedSOLAccount}
                <br />
                {allPages.searchForNewAddress}
              </p>
            </div>
          </div>
        </EmptyAccountWarningContent>
      </EmptyAccountWarningContentContainer>
    );
  }

  return connected ? (
    <>
      <div>
        <DashboardUtils />
        <ViewHeader>
          <img src={DashboardIcon} alt="" width={34} height={34} style={{ marginRight: 10, marginBottom: 5 }} />
          {dashboard.title}
        </ViewHeader>
        <Row>
          <StyledCol span={24}>
            <PortfolioOverview getPanelKey={getPanelKey} onPanelClick={onPanelClick} activePanels={activePanels} />
          </StyledCol>
        </Row>
        <Row>
          <StyledCol span={24}>
            <Element name="rewardOptions">
              <RewardOptions getPanelKey={getPanelKey} onPanelClick={onPanelClick} activePanels={activePanels} />
            </Element>
          </StyledCol>
        </Row>
        <Row>
          <StyledCol span={24}>
            <Element name="balances">
              <Balances getPanelKey={getPanelKey} onPanelClick={onPanelClick} activePanels={activePanels} />
            </Element>
          </StyledCol>
        </Row>
        <Row>
          <StyledCol span={24}>
            <Element name="dex">
              <DEXGrid getPanelKey={getPanelKey} onPanelClick={onPanelClick} activePanels={activePanels} />
            </Element>
          </StyledCol>
        </Row>
        <Row>
          <StyledCol span={24}>
            <Element name="liquidity">
              <LPGrid getPanelKey={getPanelKey} onPanelClick={onPanelClick} activePanels={activePanels} />
            </Element>
          </StyledCol>
        </Row>
        <Row>
          <StyledCol span={24}>
            <Element name="staking">
              <Staking getPanelKey={getPanelKey} onPanelClick={onPanelClick} activePanels={activePanels} />
            </Element>
          </StyledCol>
        </Row>
        <Row>
          <StyledCol span={24}>
            <Element name="farms">
              <FarmGrid getPanelKey={getPanelKey} onPanelClick={onPanelClick} activePanels={activePanels} />
            </Element>
          </StyledCol>
        </Row>
        <Row>
          <StyledCol span={24}>
            <Element name="vaults">
              <YieldAggGrid getPanelKey={getPanelKey} onPanelClick={onPanelClick} activePanels={activePanels} />
            </Element>
          </StyledCol>
        </Row>
        <Row>
          <StyledCol span={24}>
            <Element name="margin">
              <MarginTrading getPanelKey={getPanelKey} onPanelClick={onPanelClick} activePanels={activePanels} />
            </Element>
          </StyledCol>
        </Row>
        <Row>
          <StyledCol span={24}>
            <Element name="managed-funds">
              <ManagedFunds getPanelKey={getPanelKey} onPanelClick={onPanelClick} activePanels={activePanels} />
            </Element>
          </StyledCol>
        </Row>
        <Row>
          <StyledCol span={24}>
            <Element name="lending">
              <Lending getPanelKey={getPanelKey} onPanelClick={onPanelClick} activePanels={activePanels} />
            </Element>
          </StyledCol>
        </Row>
        <Row>
          <StyledCol span={24}>
            <Element name="validators">
              <ValidatorStaking getPanelKey={getPanelKey} onPanelClick={onPanelClick} activePanels={activePanels} />
            </Element>
          </StyledCol>
        </Row>
        <Row>
          <StyledCol span={24}>
            <Element name="nft-markets">
              <NFTMarkets getPanelKey={getPanelKey} onPanelClick={onPanelClick} activePanels={activePanels} />
            </Element>
          </StyledCol>
        </Row>
        <Row>
          <StyledCol span={24}>
            <Element name="nft">
              {userNFTs.size > 0 && (
                <NFTGrid getPanelKey={getPanelKey} onPanelClick={onPanelClick} activePanels={activePanels} />
              )}
            </Element>
          </StyledCol>
        </Row>
        <Row>
          <StyledCol span={24}>
            <br />
            <br />
            <br />
          </StyledCol>
        </Row>
      </div>
      <FooterButtonDiv>
        <div className="panels-controls-container">
          {activePanels.length === 0 ? (
            <StepSolidButton icon={<OpenArrows />} onClick={expandAllPanels}>
              Open All
            </StepSolidButton>
          ) : (
            <StepSolidButton icon={<CloseArrows />} onClick={closeAllPanels}>
              Close All
            </StepSolidButton>
          )}
        </div>
        <div className="back-to-top-container">
          <StepSolidButton onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            <UpArrow />
          </StepSolidButton>
        </div>
      </FooterButtonDiv>
    </>
  ) : (
    <>
      <ConnectView />
      <DashboardUtils />
    </>
  );
};

const FooterButtonDiv = styled.div`
  display: flex;
  position: fixed;
  justify-content: space-between;
  right: 20px;
  bottom: 20px;
  z-index: 4;
  .back-to-top-container {
    opacity: 0.8;
    margin-left: 10px;
  }
  .panels-controls-container {
    opacity: 0.8;
  }
`;

const StyledCol = styled(Col)`
  min-height: 0px !important;
`;

const EmptyAccountWarningContentContainer = styled.div`
  display: flex;
  height: calc(100vh - 75px - 62px);
`;

const EmptyAccountWarningContent = styled.div`
  margin: auto;
  display: flex;
  flex-direction: column;

  > img,
  > div {
    margin: 0 auto;
  }

  > div {
    display: flex;
    flex-direction: column;

    > .bold-font-container {
      height: 48px;
      margin-bottom: 10px;

      @media (max-width: 576px) {
        width: 382px;
        height: 64px;
        margin-bottom: 26px;
      }

      > p {
        font-weight: 800;
        font-size: 32px;
        line-height: 35px;
        color: #ffffff;
        text-align: center;
      }
    }

    > .paragraph-text-container {
      > p {
        font-size: 14px;
        line-height: 24px;
        color: #b2b2b2;
        text-align: center;
      }
    }
  }
`;

export default HomeView;
